/**
 * Pure helpers for the self-scoped "My Usage" surfaces (AIR-643): the profile
 * card and the quiet chat budget cue. No I/O — the server action feeds these
 * raw aggregates from litellm_usage_log and they own the period math, the
 * cap-percentage + amber threshold, and the 30-day day bucketing. Follows the
 * lib/api-keys/usage-trend.ts pure-logic precedent so both can be unit tested
 * without a database.
 */

/** Days covered by the profile spend sparkline (inclusive of today, UTC). */
export const MY_USAGE_SPEND_WINDOW_DAYS = 30;

/** A cap is "near" its limit — chat cue turns amber — at or above this share. */
export const CAP_AMBER_THRESHOLD = 0.8;

const DAY_MS = 86_400_000;

/** One UTC day's spend in USD. Sparse: days without spend are omitted. */
export interface DailySpend {
  /** UTC day key, `YYYY-MM-DD`. */
  day: string;
  spendUsd: number;
}

/** UTC midnight for a date, in epoch ms. */
function utcDayStart(date: Date): number {
  return Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
}

/**
 * Expand sparse `YYYY-MM-DD` spend into a dense `windowDays`-slot array,
 * oldest day first, with today (UTC) in the last slot. Days outside the
 * window and unparseable day keys are ignored. Mirrors bucketDailyCounts so
 * the sparkline reads the same left-to-right timeline as the API-key tables.
 */
export function bucketDailySpend(
  dailySpend: DailySpend[],
  now: Date,
  windowDays: number = MY_USAGE_SPEND_WINDOW_DAYS,
): number[] {
  const buckets = new Array<number>(windowDays).fill(0);
  const todayStart = utcDayStart(now);

  for (const { day, spendUsd } of dailySpend) {
    const dayStart = Date.parse(`${day}T00:00:00Z`);
    if (Number.isNaN(dayStart)) continue;
    if (!Number.isFinite(spendUsd) || spendUsd <= 0) continue;
    const daysAgo = Math.round((todayStart - dayStart) / DAY_MS);
    if (daysAgo < 0 || daysAgo >= windowDays) continue;
    buckets[windowDays - 1 - daysAgo] += spendUsd;
  }

  return buckets;
}

/** How a spend cap is tracking. `null` limit/spend collapses to a safe zero. */
export interface CapStatus {
  /** Clamped 0–100 for a progress bar. */
  percent: number;
  /** Raw ratio (can exceed 1 when over budget); useful for messaging. */
  ratio: number;
  /** True once spend is at or over the amber threshold — the cue turns amber. */
  isNearCap: boolean;
  /** True once spend has met or passed the cap. */
  isOverCap: boolean;
}

/**
 * Cap tracking from a spend and a limit. A zero or non-positive limit is
 * treated as fully consumed (100%) — an admin who sets a $0 cap has blocked
 * spend, not left it unlimited (unlimited is the no-cap case, handled by the
 * caller passing no budget at all).
 */
export function computeCapStatus(spendUsd: number, limitUsd: number): CapStatus {
  const spend = Number.isFinite(spendUsd) && spendUsd > 0 ? spendUsd : 0;
  const limit = Number.isFinite(limitUsd) ? limitUsd : 0;

  if (limit <= 0) {
    return { percent: 100, ratio: 1, isNearCap: true, isOverCap: true };
  }

  const ratio = spend / limit;
  return {
    percent: Math.min(100, Math.max(0, ratio * 100)),
    ratio,
    isNearCap: ratio >= CAP_AMBER_THRESHOLD,
    isOverCap: ratio >= 1,
  };
}
